import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@heyroot/ui/primitives/Badge';
import { Checkbox } from '@heyroot/ui/primitives/Checkbox';
import { Input } from '@heyroot/ui/primitives/Input';
import { Skeleton } from '@heyroot/ui/primitives/Skeleton';
import { fetchTenantPermissions, queryKeys } from '../../lib/api';

// Divergence D6: the TENANT permission vocabulary, a separate namespace from
// the admin bitmap catalogue PermissionBitPicker renders (packages/contracts'
// PERMISSION_METADATA vs apps/admin/src/rbac/permission-catalog.ts). Do NOT
// reuse PermissionBitPicker here -- sharing it would imply the two RBAC
// systems are one.
export interface TenantPermissionPickerProps {
  selected: string[];
  onChange: (next: string[]) => void;
  readOnly?: boolean;
}

const OTHER_GROUP = 'other';

export function TenantPermissionPicker({
  selected,
  onChange,
  readOnly = false,
}: TenantPermissionPickerProps) {
  const [query, setQuery] = useState('');
  const { data, isPending, isError } = useQuery({
    queryKey: queryKeys.tenantPermissions,
    queryFn: fetchTenantPermissions,
    staleTime: Infinity,
  });
  const permissions = data?.permissions ?? [];
  const selectedSet = new Set(selected);

  // PERMISSION_METADATA is a SIDECAR (~484 curated entries), not the full
  // catalogue -- ROLE_REGISTRY is upstream and module.* wildcards are
  // first-class there. An existing role's permissions array can carry a
  // value this map doesn't know about (most commonly a wildcard); render it
  // under "Other" by its raw string rather than silently dropping it from
  // view.
  const byModule = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, { name: string; label: string }[]>();
    const known = new Set(permissions.map((p) => p.name));
    for (const p of permissions) {
      if (
        q &&
        !p.name.toLowerCase().includes(q) &&
        !(p.description ?? '').toLowerCase().includes(q)
      )
        continue;
      const group = p.module ?? OTHER_GROUP;
      const list = map.get(group) ?? [];
      list.push({ name: p.name, label: p.description ?? p.name });
      map.set(group, list);
    }
    // Any SELECTED permission absent from the catalogue (e.g. a wildcard on
    // an existing role) still needs to render somewhere -- "Other", raw string.
    for (const s of selected) {
      if (known.has(s)) continue;
      if (q && !s.toLowerCase().includes(q)) continue;
      const list = map.get(OTHER_GROUP) ?? [];
      if (!list.some((e) => e.name === s)) list.push({ name: s, label: s });
      map.set(OTHER_GROUP, list);
    }
    return map;
  }, [permissions, query, selected]);

  function toggle(name: string) {
    if (readOnly) return;
    onChange(selectedSet.has(name) ? selected.filter((n) => n !== name) : [...selected, name]);
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <p role="alert" className="text-body-sm text-danger-text">
        Could not load the permission catalog. Reload the page to try again.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search permissions"
          aria-label="Search tenant permissions"
          className="max-w-xs"
        />
        <Badge variant="neutral">{selected.length} selected</Badge>
      </div>
      {Array.from(byModule.entries()).map(([group, entries]) => (
        <section key={group} className="rounded-control border border-border bg-surface p-3">
          <h4 className="text-eyebrow uppercase text-muted mb-2">{group}</h4>
          <ul className="flex flex-col gap-1.5">
            {entries.map((entry) => (
              <li key={entry.name} className="flex items-center gap-2">
                <Checkbox
                  checked={selectedSet.has(entry.name)}
                  disabled={readOnly}
                  aria-label={entry.label}
                  onChange={() => toggle(entry.name)}
                />
                <span className="text-body-sm text-primary">{entry.label}</span>
                {entry.label !== entry.name && (
                  <span className="font-mono text-caption text-muted">{entry.name}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
      {byModule.size === 0 && (
        <p className="text-body-sm text-muted">No permissions match “{query}”.</p>
      )}
    </div>
  );
}
